'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Metric {
  id: number;
  category: string;
  label: string;
  value: string;
  change: string;
  trend: 'up' | 'down' | 'stable';
  unit: string;
}

interface MetricsDashboardProps {
  className?: string;
}

const MetricsDashboard = ({ className = '' }: MetricsDashboardProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('All');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const metrics: Metric[] = [
    { id: 1, category: 'Economic', label: 'Direct Jobs Created', value: '12,450', change: '+18%', trend: 'up', unit: 'positions' },
    { id: 2, category: 'Economic', label: 'Average Income Increase', value: '42%', change: '+8%', trend: 'up', unit: 'growth' },
    { id: 3, category: 'Economic', label: 'Local Procurement', value: '$24.5M', change: '+22%', trend: 'up', unit: 'USD' },
    { id: 4, category: 'Economic', label: 'SME Partnerships', value: '156', change: '+31%', trend: 'up', unit: 'businesses' },
    { id: 5, category: 'Infrastructure', label: 'Projects Completed', value: '23', change: '+5', trend: 'up', unit: 'projects' },
    { id: 6, category: 'Infrastructure', label: 'Communities Served', value: '89,000', change: '+12%', trend: 'up', unit: 'people' },
    { id: 7, category: 'Infrastructure', label: 'Infrastructure Investment', value: '$156M', change: '+28%', trend: 'up', unit: 'USD' },
    { id: 8, category: 'Infrastructure', label: 'Service Reliability', value: '94%', change: '+3%', trend: 'up', unit: 'uptime' },
    { id: 9, category: 'Environmental', label: 'Carbon Reduction', value: '34,500', change: '+15%', trend: 'up', unit: 'tons CO2' },
    { id: 10, category: 'Environmental', label: 'Renewable Energy', value: '67%', change: '+11%', trend: 'up', unit: 'portfolio' },
    { id: 11, category: 'Environmental', label: 'Water Conservation', value: '2.8M', change: '+9%', trend: 'up', unit: 'liters' },
    { id: 12, category: 'Environmental', label: 'Waste Reduction', value: '78%', change: '+6%', trend: 'up', unit: 'reduction' },
    { id: 13, category: 'Knowledge', label: 'Training Programs', value: '142', change: '+24%', trend: 'up', unit: 'programs' },
    { id: 14, category: 'Knowledge', label: 'Participants Trained', value: '8,900', change: '+19%', trend: 'up', unit: 'people' },
    { id: 15, category: 'Knowledge', label: 'Local Leadership', value: '89%', change: '+7%', trend: 'up', unit: 'positions' },
    { id: 16, category: 'Knowledge', label: 'Certification Rate', value: '92%', change: '+4%', trend: 'up', unit: 'completion' },
    { id: 17, category: 'Financial', label: 'Portfolio ROI', value: '14.2%', change: '+1.8%', trend: 'up', unit: 'annual' },
    { id: 18, category: 'Financial', label: 'Capital Deployed', value: '$342M', change: '+26%', trend: 'up', unit: 'USD' },
    { id: 19, category: 'Financial', label: 'Risk-Adjusted Return', value: '11.8%', change: '+1.2%', trend: 'up', unit: 'annual' },
    { id: 20, category: 'Financial', label: 'Portfolio Diversification', value: '8', change: '+2', trend: 'up', unit: 'sectors' }
  ];

  const categories = ['All', 'Economic', 'Infrastructure', 'Environmental', 'Knowledge', 'Financial'];

  const filteredMetrics = selectedCategory === 'All' 
    ? metrics 
    : metrics.filter(m => m.category === selectedCategory);

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up':
        return 'ArrowTrendingUpIcon';
      case 'down':
        return 'ArrowTrendingDownIcon';
      default:
        return 'MinusIcon';
    }
  };

  const getTrendColor = (trend: string) => {
    switch (trend) {
      case 'up':
        return 'text-success';
      case 'down':
        return 'text-error';
      default:
        return 'text-muted-foreground';
    }
  };

  if (!isHydrated) {
    return (
      <section className={`py-20 bg-muted ${className}`}>
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mb-12">
            <h2 className="font-headline text-4xl md:text-5xl font-semibold text-foreground mb-6">
              Impact Metrics Dashboard
            </h2>
            <p className="font-body text-lg text-text-secondary leading-relaxed">
              Real-time tracking of key performance indicators across all impact pillars, updated quarterly with third-party verification.
            </p>
          </div>
          <div className="animate-pulse space-y-4">
            <div className="h-12 bg-card rounded-lg"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="h-40 bg-card rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={`py-20 bg-muted ${className}`}>
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mb-12">
          <h2 className="font-headline text-4xl md:text-5xl font-semibold text-foreground mb-6">
            Impact Metrics Dashboard
          </h2>
          <p className="font-body text-lg text-text-secondary leading-relaxed">
            Real-time tracking of key performance indicators across all impact pillars, updated quarterly with third-party verification.
          </p>
        </div>

        <div className="mb-8 flex flex-wrap gap-3">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-6 py-2.5 rounded-lg font-body font-medium text-sm transition-all duration-300 ${
                selectedCategory === category
                  ? 'bg-primary text-white shadow-institutional'
                  : 'bg-card text-text-secondary hover:bg-border'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {filteredMetrics.map((metric) => (
            <div
              key={metric.id}
              className="bg-card border border-border rounded-lg p-6 hover:shadow-institutional transition-shadow duration-300"
            >
              <div className="flex items-start justify-between mb-4">
                <span className="inline-block px-3 py-1 bg-muted rounded-full font-body text-xs font-medium text-text-secondary">
                  {metric.category}
                </span>
                <Icon
                  name={getTrendIcon(metric.trend) as any}
                  size={20}
                  variant="solid"
                  className={getTrendColor(metric.trend)}
                />
              </div>
              <h3 className="font-body text-sm text-text-secondary mb-2">
                {metric.label}
              </h3>
              <p className="font-headline text-3xl font-semibold text-foreground mb-1">
                {metric.value}
              </p>
              <p className="font-body text-xs text-muted-foreground">
                {metric.unit}
              </p>
              <div className="mt-4 pt-4 border-t border-border">
                <p className={`font-body text-sm font-medium ${getTrendColor(metric.trend)}`}>
                  {metric.change} vs. previous quarter
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 bg-card border border-border rounded-lg p-8">
          <div className="flex items-start space-x-4">
            <Icon name="InformationCircleIcon" size={24} variant="solid" className="text-primary flex-shrink-0 mt-1" />
            <div>
              <h4 className="font-headline text-xl font-semibold text-foreground mb-2">
                Data Verification & Reporting
              </h4>
              <p className="font-body text-text-secondary leading-relaxed">
                All metrics are independently verified by third-party auditors on a quarterly basis. Complete methodology documentation, data sources, and verification reports are available to institutional investors and government partners through our secure partnership portal.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MetricsDashboard;